import { Component, Injector, Input, OnInit } from '@angular/core';
import { WidgetResidualCalculationService } from '../../../../../services/WidgetResidualCalculationService';
import { WidgetBaseComponent } from '../../widgetBase';

/**
 * CalculationOfThePotentialExitValue Widget
 */
@Component({
  selector: 'calculation-of-the-potential-exit-value-widget',
  templateUrl: './calculation-of-the-potential-exit-value-widget.component.html',
  styleUrls: ['../../common/css/general.scss', './calculation-of-the-potential-exit-value-widget.component.scss']
})
export class CalculationOfThePotentialExitValueWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Reference list data
   */
  reflistDropDown = {};

  /**
   * Versions Title Override (obsolete?)
   */
  public versionsTitleOverride = 'residualValue';

  /**
   * Report Designer Locale (obsolete?)
   */
  public reportDesignerLocale = null;

  /**
   * Version (obsolete?)
   */
  public version = null;

  /**
  * Constructor
  * @param widgetResidualCalculationService WidgetResidualCalculationService
  */
  constructor(injector: Injector,
    private widgetResidualCalculationService: WidgetResidualCalculationService) {
    super(injector, 'calculationOfThePotentialExitValue', false);
    //override default translationName (which equals to name)
    this.translationName = 'residualValueCalculation';
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.initWidgetConfig();
    this.widgetResized();

    this.widgetService.loadReflist('ref_unit_type').subscribe((reflistsData: any) => {
      this.reflistDropDown['ref_unit_type'] = reflistsData;
    });
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.onEvents.push(this.eventBus.subscribeEvent('saveChanges', () => {
      if (!this.isDirty) {
        this.saveChangesHandled();
      } else {
        this.widgetResidualCalculationService.save().then(() => {
          this.setDirtyFlag(false);
          this.saveChangesHandled();
        });
      }
    }));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (!this.widgetConfig.hashtagSupported) {
      this.widgetConfig.hashtagSupported = true;
    }
    this.currentLayout = this.widgetConfig.widgetSettings.currentLayout;
    this.availableLayouts = this.widgetConfig.widgetSettings.availableLayouts;
    this.currentCalculation = this.widgetConfig.widgetSettings.currentCalculation;

    this.mockup ?
      this.widgetResidualCalculationService.getMockupData().then((data: any) => {
        this.widgetData = this.preprocessLoadedData(data);
        this.onFieldChanged(); //Re-calc mockup data
      })
      : this.widgetResidualCalculationService.load(this.getReport()).then((data: any) => {
        this.widgetData = this.preprocessLoadedData(data);
        this.page.widgetDataLoaded(this.widgetConfig.widgetSettings.id);
      });
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  preprocessLoadedData(data) {
    data.WidgetLayout = this.currentLayout;
    this.setDirtyFlag(false);

    return data[this.currentCalculation];
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  /**
   * Save function - obsolete?
   */
  private save() {
    this.widgetResidualCalculationService.save();
  }

  /**
   * Calculation changed event handler
  * @param newCalculation {number} New Calculation
   */
  public calculationChanged(newCalculation: number): void {
    try {
      if (this.widgetConfig?.widgetSettings?.currentCalculation) {
        this.widgetConfig.widgetSettings.currentCalculation = newCalculation;
        this.currentCalculation = newCalculation;

        this.widgetData = null;
        setTimeout(() => {
          this.mockup ?
            this.widgetResidualCalculationService.getMockupData().then((data: any) => {
              this.widgetData = this.preprocessLoadedData(data);
            })
            : this.widgetResidualCalculationService.load(this.getReport()).then((data: any) => {
              this.widgetData = this.preprocessLoadedData(data);
            });
          this.setDirtyFlag(false);
          this.onFieldChanged();
        });
      }
    } catch (e) {
      // Nothing
    }
  }

  /**
   * Layout changed event handler
  * @param newLayout {string} New layout
   */
  public layoutChanged(newLayout: string): void {
    try {
      if (this.widgetConfig?.widgetSettings?.layoutSizeConfiguration) {
        const config = this.widgetConfig.widgetSettings.layoutSizeConfiguration[newLayout];
        if (config.x) {
          this.widgetConfig.minSizeX = config.x;
          this.widgetConfig.sizeX = config.x;

        }
        if (config.y) {
          this.widgetConfig.sizeY = config.y;
        }
      }
      this.widgetConfig.widgetSettings.currentLayout = newLayout;
    } catch (e) {
      // Nothing
    }
    if (this.widgetData) {
      this.widgetData.WidgetLayout = newLayout;
      this.onFieldChanged();
    }
  }

  /**
   * TrackBy function used by Angular *ngFor trackBy
   * @param _index {number} Index
   * @param el {any} Element
   */
  public trackById(_index: number, el: any): number {
    return el.trackId;
  }

  /**
   * CalculationOfThePotentialExitValue Add clicked event handler
   */
  public calculationOfThePotentialExitValueAddClicked() {
    const row = {
      Oid: -1,
      ReportId: this.rdStorage.getReport(),
      UseCategoryId: "",
      LettableArea: 0,
      UnitsQuantity: 0,
      MarketRentEuroPerQmPerMonth: 0,
      MarketRentEuroPerYearCalc: 0
    };

    // add to observable array
    this.widgetData.LettingScenarios.push(row);
    this.setDirtyFlag(true);
  }

  /**
   * CalculationOfThePotentialExitValue Sales Add clicked event handler
   */
  public calculationOfThePotentialExitValueSalesAddClicked() {
    const row = {
      Oid: -1,
      ReportId: this.rdStorage.getReport(),
      UseCategoryId: "",
      LettableArea: 0,
      UnitsQuantity: 0,
      SalesPriceEuroQm: 0,
      SalesPriceTotalCalc: 0
    };

    // add to observable array
    this.widgetData.SalesScenarios.push(row);
    this.setDirtyFlag(true);
  }

  /**
   * CalculationOfThePotentialExitValue Delete clicked event handler
   */
  public calculationOfThePotentialExitValueDeleteClicked() {
    const checkedRows = this.widgetData.LettingScenarios.filter((val) => {
      return val.checked;
    });
    checkedRows.forEach(row => {
      this.widgetData.LettingScenarios = this.widgetData.LettingScenarios.filter((item) => {
        return item !== row
      });
    });
    this.setDirtyFlag(true);
    this.onFieldChanged();
  }

  /**
   * CalculationOfThePotentialExitValue Sales Delete clicked event handler
   */
  public calculationOfThePotentialExitValueSalesDeleteClicked() {
    const checkedRows = this.widgetData.SalesScenarios.filter((val) => {
      return val.checked;
    });
    checkedRows.forEach(row => {
      this.widgetData.SalesScenarios = this.widgetData.SalesScenarios.filter((item) => {
        return item !== row
      });
    });
    this.setDirtyFlag(true);
    this.onFieldChanged();
  }

  /**
   * Returns placeholders
   */
  public getPlaceholders(inPlaceData, otherData) {
    let countingInPlaceData;
    let countingOtherData;
    if (this.page.editMode) {
      countingInPlaceData = inPlaceData;
      countingOtherData = otherData;
    }
    else {
      countingInPlaceData = inPlaceData.filter((entry) => { return entry.CategoryFormated != 0 && entry.CategoryFormated != null && ((entry.LettableArea != 0 && entry.LettableArea != null) || (entry.UnitsQuantity != 0 && entry.UnitsQuantity != null)); });
      countingOtherData = otherData.filter((entry) => { return entry.CategoryFormated != 0 && entry.CategoryFormated != null && ((entry.LettableArea != 0 && entry.LettableArea != null) || (entry.UnitsQuantity != 0 && entry.UnitsQuantity != null)); });
    }

    const ret = [];
    for (let i = 0; i < countingOtherData.length - countingInPlaceData.length; ++i) {
      ret.push(i);
    }
    return ret;
  }

  /**
   * Field changed event handler
   */
  public onFieldChanged() {
    this.widgetData = this.widgetResidualCalculationService.calculate(this, null);
  }
}
